import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { Toaster } from "@/components/ui/sonner";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CartSheet from "@/components/CartSheet";
import ScrollToTop from "@/components/ScrollToTop";
import LocationTracker from "@/components/LocationTracker";

import Home from "@/pages/Home";
import Services from "@/pages/Services";
import Quote from "@/pages/Quote";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Checkout from "@/pages/Checkout";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import AuthCallback from "@/pages/AuthCallback";
import OrderSuccess from "@/pages/OrderSuccess";
import Pay from "@/pages/Pay";

import DashboardLayout from "@/pages/dashboard/DashboardLayout";
import DashboardHome from "@/pages/dashboard/DashboardHome";
import SetupWizard from "@/pages/dashboard/SetupWizard";
import QuotesInbox from "@/pages/dashboard/QuotesInbox";
import OrdersList from "@/pages/dashboard/OrdersList";
import ProductsAdmin from "@/pages/dashboard/ProductsAdmin";
import SettingsAdmin from "@/pages/dashboard/SettingsAdmin";
import UsersAdmin from "@/pages/dashboard/UsersAdmin";
import MyJobs from "@/pages/dashboard/MyJobs";
import TechMap from "@/pages/dashboard/TechMap";
import InventoryAdmin from "@/pages/dashboard/InventoryAdmin";
import MyAccount from "@/pages/MyAccount";

function PublicLayout({ children }) {
  return (
    <div className="App flex min-h-screen flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
      <CartSheet />
    </div>
  );
}

function ProtectedOwner({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex h-screen items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "owner") return <Navigate to="/" replace />;
  return children;
}

function ProtectedDashboard({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex h-screen items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!["technician", "admin", "owner"].includes(user.role)) return <Navigate to="/account" replace />;
  return children;
}

function StaffOnlyRoute({ allow, children }) {
  const { user } = useAuth();
  if (!allow.includes(user?.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

const STAFF_ALL = ["admin", "owner"];

function OwnerOnlyRoute({ children }) {
  const { user } = useAuth();
  if (user?.role !== "owner") return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <>
      <ScrollToTop />
      <LocationTracker />
      <Routes>
      <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
      <Route path="/services" element={<PublicLayout><Services /></PublicLayout>} />
      <Route path="/quote" element={<PublicLayout><Quote /></PublicLayout>} />
      <Route path="/shop" element={<PublicLayout><Shop /></PublicLayout>} />
      <Route path="/shop/:id" element={<PublicLayout><ProductDetail /></PublicLayout>} />
      <Route path="/checkout" element={<PublicLayout><Checkout /></PublicLayout>} />
      <Route path="/order-success" element={<PublicLayout><OrderSuccess /></PublicLayout>} />
      <Route path="/pay/:orderId" element={<PublicLayout><Pay /></PublicLayout>} />
      <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
      <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
      <Route path="/login" element={<PublicLayout><Login /></PublicLayout>} />
      <Route path="/signup" element={<PublicLayout><Signup /></PublicLayout>} />

        <Route path="/account" element={<PublicLayout><MyAccount /></PublicLayout>} />

        <Route path="/dashboard" element={<ProtectedDashboard><DashboardLayout /></ProtectedDashboard>}>
          <Route index element={<DashboardHome />} />
          <Route path="setup" element={<OwnerOnlyRoute><SetupWizard /></OwnerOnlyRoute>} />
          <Route path="jobs" element={<MyJobs />} />
          <Route path="map" element={<StaffOnlyRoute allow={STAFF_ALL}><TechMap /></StaffOnlyRoute>} />
          <Route path="quotes" element={<StaffOnlyRoute allow={STAFF_ALL}><QuotesInbox /></StaffOnlyRoute>} />
          <Route path="orders" element={<StaffOnlyRoute allow={STAFF_ALL}><OrdersList /></StaffOnlyRoute>} />
          <Route path="products" element={<StaffOnlyRoute allow={STAFF_ALL}><ProductsAdmin /></StaffOnlyRoute>} />
          <Route path="inventory" element={<StaffOnlyRoute allow={STAFF_ALL}><InventoryAdmin /></StaffOnlyRoute>} />
          <Route path="users" element={<StaffOnlyRoute allow={STAFF_ALL}><UsersAdmin /></StaffOnlyRoute>} />
          <Route path="settings" element={<OwnerOnlyRoute><SettingsAdmin /></OwnerOnlyRoute>} />
        </Route>
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster richColors closeButton position="top-right" />
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}
